import { useEffect, useRef, useState } from 'react';
import { NAV_TREE, trunkOf } from '@/lib/navTree';

// Swipe order on phones: Home, then each trunk's main page in menu order.
const ORDER = [{ id: 'home', label: 'Home', landing: 'home' }, ...NAV_TREE.map((t) => ({ id: t.id, label: t.label, landing: t.landing }))];
const MIN_DX = 70;
const MAX_MS = 700;
const EDGE = 24; // leave the screen edges to the phone's own back/forward gestures

const scrollsSideways = (el) => {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (n.matches?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="slider"], [data-no-swipe], table')) return true;
    const ox = getComputedStyle(n).overflowX;
    if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 2) return true;
  }
  return false;
};

export function useSwipeNav(activeSection, navigate) {
  const [hint, setHint] = useState(null);
  const start = useRef(null);
  const sectionRef = useRef(activeSection);
  sectionRef.current = activeSection;
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    const onStart = (e) => {
      const t = e.touches[0];
      start.current = (window.innerWidth >= 1024 || e.touches.length > 1 || t.clientX < EDGE || t.clientX > window.innerWidth - EDGE
        || scrollsSideways(e.target)) ? null : { x: t.clientX, y: t.clientY, at: Date.now() };
    };
    const onEnd = (e) => {
      const s = start.current;
      start.current = null;
      if (!s || window.getSelection()?.toString()) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < MIN_DX || Math.abs(dx) < 2 * Math.abs(dy) || Date.now() - s.at > MAX_MS) return;
      const cur = sectionRef.current === 'home' ? 'home' : trunkOf(sectionRef.current);
      const i = ORDER.findIndex((o) => o.id === cur);
      if (i < 0) return;
      const next = ORDER[i + (dx < 0 ? 1 : -1)];
      if (!next) return;
      navRef.current(next.landing);
      setHint({ label: next.label, dir: dx < 0 ? 'left' : 'right', key: Date.now() });
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);

  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => setHint(null), 900);
    return () => clearTimeout(t);
  }, [hint]);

  return hint;
}

export const SwipeHint = ({ hint }) => (hint ? (
  <div key={hint.key} className="lg:hidden fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none rounded-full bg-foreground/85 text-background text-sm font-medium px-4 py-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-150"
    data-testid="swipe-hint">
    {hint.dir === 'left' ? `${hint.label} →` : `← ${hint.label}`}
  </div>
) : null);
